import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { useCart } from '@/lib/cart';
import { useSEO } from '@/lib/useSEO';
import { resolveImageUrl } from '@/components/ImageUploader';
import { formatCOP, formatDate } from '@/lib/format';
import { Star, ShoppingCart, Store, MapPin, ZoomIn } from 'lucide-react';
import { ContactSellerButton } from '@/components/ContactSellerButton';
import { toast } from 'sonner';
import { PRODUCT } from '@/constants/testIds';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';

const PLACEHOLDER = 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800';

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => setData(data)).catch(() => {});
  }, [id]);

  const product = data?.product;
  const reviews = data?.reviews || [];
  const avgRating = reviews.length
    ? Number((reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1))
    : null;

  useSEO(product ? {
    title: `${product.name} ${product.brand} ${product.reference} | AutoMarket Colombia`,
    description: (product.description || `Compra ${product.name} (${product.brand}, ref ${product.reference}) en AutoMarket Colombia. ${product.stock > 0 ? 'Stock disponible' : 'Consulta disponibilidad'}.`).slice(0, 160),
    image: (product.images || [])[0],
    canonical: `/producto/${product.slug || product.product_id}`,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      sku: product.reference,
      brand: { '@type': 'Brand', name: product.brand },
      description: product.description,
      image: product.images || [],
      offers: {
        '@type': 'Offer',
        priceCurrency: 'COP',
        price: product.has_promotion ? product.effective_price : product.price,
        availability: product.stock > 0
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        url: window.location.href,
      },
      ...(avgRating ? {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: avgRating,
          reviewCount: reviews.length,
        },
      } : {}),
    },
  } : { title: 'Cargando producto…', canonical: window.location.pathname });

  if (!data) {
    return <Layout><div className="p-12 text-center text-slate-500">Cargando...</div></Layout>;
  }
  const { seller, seller_profile } = data;
  const images = product.images?.length ? product.images.map(resolveImageUrl) : [PLACEHOLDER];

  const onAdd = () => {
    add(product, qty);
    toast.success(`${qty} × ${product.name} agregado`);
  };
  const onBuyNow = () => { onAdd(); nav('/carrito'); };

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500 mb-4">
          {product.brand} · {product.category}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr,1fr] gap-10">
          {/* Gallery */}
          <div data-testid={PRODUCT.gallery}>
            <Dialog>
              <DialogTrigger asChild>
                <button
                  type="button"
                  data-testid="product-image-zoom-trigger"
                  aria-label="Ampliar imagen"
                  className="group relative block w-full aspect-square border border-slate-200 bg-slate-50 overflow-hidden cursor-zoom-in"
                >
                  <img
                    src={images[active]}
                    alt={product.name}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
                  />
                  <span className="pointer-events-none absolute right-3 top-3 inline-flex items-center gap-1 bg-white/90 backdrop-blur px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-700 shadow-sm">
                    <ZoomIn className="h-3 w-3" /> Ampliar
                  </span>
                </button>
              </DialogTrigger>
              <DialogContent
                data-testid="product-image-zoom-dialog"
                className="max-w-5xl w-[95vw] p-0 bg-black border-none"
              >
                <img
                  src={images[active]}
                  alt={product.name}
                  className="w-full h-auto max-h-[85vh] object-contain"
                  onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
                />
              </DialogContent>
            </Dialog>
            {images.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto">
                {images.map((img, i) => (
                  <button
                    key={i}
                    data-testid={PRODUCT.galleryThumb(i)}
                    onClick={() => setActive(i)}
                    className={`h-20 w-20 shrink-0 border ${i === active ? 'border-slate-900' : 'border-slate-200'} bg-slate-50 overflow-hidden`}
                  >
                    <img src={img} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <h1 className="font-display text-3xl font-black tracking-tight text-slate-900 leading-tight">
              {product.name}
            </h1>
            <div className="mt-2 flex items-center gap-4 text-sm text-slate-500">
              <span className="font-mono">Ref: {product.reference}</span>
              {avgRating && (
                <span className="flex items-center gap-1">
                  <Star className="h-4 w-4 fill-orange-500 text-orange-500" />
                  <span className="font-semibold text-slate-900">{avgRating}</span>
                  <span>({reviews.length})</span>
                </span>
              )}
            </div>

            <div className="mt-6 border-y border-slate-200 py-6">
              {product.has_promotion ? (
                <>
                  <div className="flex items-center gap-3">
                    <span
                      data-testid="product-detail-price-original"
                      className="text-lg line-through text-slate-400 font-semibold"
                    >
                      {formatCOP(product.original_price)}
                    </span>
                    <span
                      data-testid="product-detail-discount-badge"
                      className="inline-flex items-center gap-0.5 bg-orange-100 text-orange-700 text-xs font-bold uppercase tracking-wider px-2 py-1 rounded"
                    >
                      −{product.discount_pct}%
                    </span>
                  </div>
                  <div
                    data-testid={PRODUCT.price}
                    className="font-display text-4xl font-black text-orange-600 mt-1"
                  >
                    {formatCOP(product.effective_price)}
                  </div>
                  <div
                    data-testid="product-detail-discount-amount"
                    className="mt-1 text-xs text-orange-700 font-semibold"
                  >
                    Ahorras {formatCOP(product.discount_amount)}
                  </div>
                </>
              ) : (
                <div data-testid={PRODUCT.price} className="font-display text-4xl font-black text-slate-900">
                  {formatCOP(product.price)}
                </div>
              )}
              <div className="mt-1 text-sm">
                {product.stock > 0
                  ? <span className="text-emerald-700 font-semibold">{product.stock} disponibles</span>
                  : <span className="text-rose-700 font-semibold">Agotado</span>}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Compatibilidad</div>
                <div className="flex flex-wrap gap-2">
                  {(product.vehicle_compat || []).map((v) => (
                    <Badge key={v} variant="outline" className="rounded-none border-slate-300 font-normal">{v}</Badge>
                  ))}
                  {(!product.vehicle_compat || product.vehicle_compat.length === 0) && (
                    <span className="text-sm text-slate-500">Universal</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Garantía</div>
                <div className="text-sm font-semibold text-slate-900" data-testid="product-warranty">
                  {product.warranty_months
                    ? `${product.warranty_months} mes${product.warranty_months > 1 ? 'es' : ''}`
                    : (product.warranty || '30 días (estándar)')}
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <div className="flex items-center border border-slate-300">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-11 w-11 hover:bg-slate-100">−</button>
                <input
                  data-testid={PRODUCT.qtyInput}
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                  className="h-11 w-14 text-center border-x border-slate-300 outline-none font-display font-bold"
                />
                <button onClick={() => setQty((q) => q + 1)} className="h-11 w-11 hover:bg-slate-100">+</button>
              </div>
              <Button
                data-testid={PRODUCT.addToCartBtn}
                onClick={onAdd}
                disabled={product.stock === 0}
                variant="outline"
                className="rounded-none border-slate-900 text-slate-900 hover:bg-slate-100 h-11 px-5"
              >
                <ShoppingCart className="mr-2 h-4 w-4" /> Agregar
              </Button>
              <Button
                data-testid={PRODUCT.buyNowBtn}
                onClick={onBuyNow}
                disabled={product.stock === 0}
                className="rounded-none bg-orange-500 hover:bg-orange-400 text-slate-900 h-11 px-5 font-bold"
              >
                Comprar ahora
              </Button>
            </div>

            {seller?.user_id && (
              <div className="mt-3 border border-slate-200 bg-slate-50 p-3 flex items-center justify-center">
                <ContactSellerButton
                  sellerId={seller.user_id}
                  sellerName={seller.business_name || seller.name}
                  productId={product.product_id}
                  dataTestId="product-contact-seller-btn"
                />
              </div>
            )}

            <div className="mt-8 border border-slate-200 p-5 bg-slate-50">
              <Link
                to={`/tienda/${seller?.user_id || product.seller_id}`}
                data-testid="product-seller-profile-link"
                className="flex items-start gap-3 hover:opacity-80 transition-opacity"
              >
                <div className="h-10 w-10 flex items-center justify-center bg-slate-900 text-white">
                  <Store className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Vendedor</div>
                  <div className="font-display font-bold text-slate-900 hover:text-blue-700">
                    {seller_profile?.business_name || seller?.name}
                  </div>
                  {seller_profile?.address && (
                    <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                      <MapPin className="h-3 w-3" /> {seller_profile.address}
                    </div>
                  )}
                  <div className="text-[11px] text-blue-700 mt-1 font-semibold">Ver perfil del vendedor →</div>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* Description + Reviews */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-[1.2fr,1fr] gap-10">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Descripción</div>
            <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{product.description}</p>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
              Calificaciones ({reviews.length})
            </div>
            {reviews.length === 0 && (
              <p className="text-sm text-slate-500">Aún sin calificaciones.</p>
            )}
            <div className="space-y-3">
              {reviews.map((r) => (
                <div key={r.review_id} className="border border-slate-200 p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-slate-900">{r.buyer_name}</div>
                    <div className="flex items-center gap-1 text-orange-500">
                      {Array.from({ length: r.rating }).map((_, i) => (
                        <Star key={i} className="h-3.5 w-3.5 fill-current" />
                      ))}
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 font-mono mt-1">{formatDate(r.created_at)}</div>
                  {r.comment && <p className="mt-2 text-sm text-slate-700">{r.comment}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
